import { ArrowRightOutlined, CheckCircleFilled } from "@ant-design/icons"
import { Button, Flex } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { brand, neutral } from "../../../../components/widgets/tokens.ts"

export type WizardStep = 1 | 2 | 3 | 4

const STEPS: { n: WizardStep; labelKey: string }[] = [
  { n: 1, labelKey: "reports.wizard.steps.general" },
  { n: 2, labelKey: "reports.wizard.steps.build" },
  { n: 3, labelKey: "reports.wizard.steps.filters" },
  { n: 4, labelKey: "reports.wizard.steps.share" }
]

interface WizardActionsProps {
  current: WizardStep
  nextLabel: string
  nextLoading?: boolean
  onCancel: () => void
  onBack: () => void
  onNext: () => void
}

export const ReportWizardActions = ({
  current,
  nextLabel,
  nextLoading,
  onCancel,
  onBack,
  onNext
}: WizardActionsProps) => {
  const { t } = useTranslation()
  return (
    <Flex gap="small" align="center">
      <Button onClick={onCancel}>{t("reports.cancel")}</Button>
      {current > 1 && (
        <Button onClick={onBack}>{t("reports.wizard.back")}</Button>
      )}
      <Button
        type="primary"
        loading={nextLoading}
        onClick={onNext}
        icon={current < 4 ? <ArrowRightOutlined /> : undefined}
        iconPosition="end"
      >
        {nextLabel}
      </Button>
    </Flex>
  )
}

interface ReportWizardHeaderProps extends WizardActionsProps {
  completed: Set<WizardStep>
}

export const ReportWizardStepper = ({
  current,
  completed
}: {
  current: WizardStep
  completed: Set<WizardStep>
}) => {
  const { t } = useTranslation()
  return (
    <Flex align="center" gap="large" wrap="wrap" justify="center">
      {STEPS.map((step) => {
        const isCurrent = step.n === current
        const isDone = completed.has(step.n)
        const color = isCurrent
          ? brand.teal
          : isDone
            ? brand.teal
            : neutral.faint
        return (
          <Flex
            key={step.n}
            align="center"
            gap="small"
            style={{
              borderBottom: isCurrent
                ? `2px solid ${brand.teal}`
                : "2px solid transparent",
              paddingBottom: 6
            }}
          >
            {isDone && !isCurrent ? (
              <CheckCircleFilled style={{ color: brand.teal, fontSize: 20 }} />
            ) : (
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  color: isCurrent ? "#fff" : neutral.muted,
                  background: isCurrent ? brand.teal : neutral.light
                }}
              >
                {step.n}
              </span>
            )}
            <span
              style={{
                fontWeight: isCurrent ? 600 : 500,
                color
              }}
            >
              {t(step.labelKey)}
            </span>
          </Flex>
        )
      })}
    </Flex>
  )
}

export const ReportWizardHeader = ({
  current,
  completed,
  nextLabel,
  nextLoading,
  onCancel,
  onBack,
  onNext
}: ReportWizardHeaderProps) => (
  <Flex align="center" justify="space-between" gap="middle" wrap="wrap">
    <div className="flex-1 min-w-0">
      <ReportWizardStepper current={current} completed={completed} />
    </div>
    <ReportWizardActions
      current={current}
      nextLabel={nextLabel}
      nextLoading={nextLoading}
      onCancel={onCancel}
      onBack={onBack}
      onNext={onNext}
    />
  </Flex>
)
